import React, { useCallback, useEffect, useRef, useState } from "react";

import PatientCard, { patientMrn } from "./PatientCard";
import CoverageCard from "./CoverageCard";
import PatientClaims from "./PatientClaims";
import DuplicatesPanel from "./DuplicatesPanel";
import { PatientAuths } from "../authorizations";
import { PayStatusChip } from "../collections";
import { OutreachTab } from "../outreach";
import StatementModal from "../../components/StatementModal";
import useCapabilities from "../../hooks/useCapabilities";
import { openLedgerWith } from "../reports/ledgerLink";
import { clearPatientLink, peekPatientLink } from "../../utils/patientLink";
import {
  addCoverage,
  checkEligibility,
  fetchPatientSummary,
  fetchPracticePayers,
  replaceCoverage,
  saveDemographics,
  searchPatients,
} from "./api";
import "./PatientsPage.css";
import { formatPatientName } from "../../utils/formatName";

// The patient as a first-class object: search on the left, the whole patient on
// the right. Not a tab inside Practice Details, because a biller looking someone
// up is not administering a practice.
//
// Rendered by App.js only when `features.patients` is on — App owns the FeatureOff
// panel, so this page never checks the gate to decide whether to exist. It reads
// `features` only to vary within itself.

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 50;

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function PatientsPage({
  selectedPractice,
  readOnly,
  isAdmin,
  features = {},
}) {
  const practiceId = selectedPractice && selectedPractice.id;

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, SEARCH_DEBOUNCE_MS);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searching, setSearching] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [payers, setPayers] = useState([]);
  // The last eligibility answer for the patient on screen, keyed to them so one
  // patient's coverage can never be read under another's name.
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  // "Generate statement" needs the statements feature. Card charging lives on
  // the A/R page, not here.
  const capabilities = useCapabilities(practiceId);
  const statementsOn = !!capabilities.features.statements;
  // Outreach (texting and emailing a patient about a balance) rides the
  // collections feature and is staff-only: the client role sees no card and
  // fetches no outreach endpoint.
  // Off for everyone (David, 2026-10-25): Atlas is not doing patient outreach, so no
  // outreach tab and no statement-email status chip. Restore the line below to undo.
  // const outreachOn = !readOnly && !!capabilities.features.collections;
  const outreachOn = false;
  const [statementOpen, setStatementOpen] = useState(false);
  // The Duplicates panel swaps in for the list + detail. Staff only: the client
  // role never sees the toggle, and the merge buttons inside need `isAdmin`,
  // mirroring the routes (list = biller, merge = admin).
  const [showDuplicates, setShowDuplicates] = useState(false);
  // Bumped after a merge so the roster re-fetches: merged rows are gone and the
  // survivor's counts changed.
  const [searchNonce, setSearchNonce] = useState(0);

  // A slow response that resolves after a newer one must not overwrite it. Both
  // fetches need this, not only the search: without it, clicking A then B can
  // leave A's record on screen under B's id — and a save from that screen writes
  // A's values onto B.
  const searchToken = useRef(0);
  const summaryToken = useRef(0);

  // Reset on a real practice change only. StrictMode runs this effect twice on
  // mount; without the guard the second run wiped a patient the first one had
  // just opened from a link. A patient name clicked on another page
  // (Appointments) is opened here, after the reset, so the two cannot race.
  const appliedPractice = useRef(undefined);
  useEffect(() => {
    if (appliedPractice.current === practiceId) return;
    appliedPractice.current = practiceId;
    setSelectedId(null);
    setSummary(null);
    setResults([]);
    setTotal(0);
    setOffset(0);
    setQuery("");
    setMessage(null);
    setShowDuplicates(false);
    const linkedId = peekPatientLink("patients", practiceId);
    if (linkedId) {
      clearPatientLink();
      openPatient(linkedId);
    }
  }, [practiceId]);

  // A new search starts from its first page.
  useEffect(() => {
    setOffset(0);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    fetchPracticePayers(practiceId)
      .then((rows) => {
        if (!cancelled) setPayers(rows);
      })
      .catch(() => {
        // Not fatal: the coverage form says so itself when the list is empty.
        if (!cancelled) setPayers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    const token = ++searchToken.current;
    setSearching(true);
    let cancelled = false;
    searchPatients(practiceId, debouncedQuery, PAGE_SIZE, offset)
      .then((data) => {
        if (cancelled || token !== searchToken.current) return;
        setResults(data.patients || []);
        setTotal(Number(data.total) || 0);
      })
      .catch((error) => {
        if (cancelled || token !== searchToken.current) return;
        setResults([]);
        setTotal(0);
        setMessage({ type: "error", text: error.message });
      })
      .finally(() => {
        if (!cancelled && token === searchToken.current) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, debouncedQuery, offset, searchNonce]);

  const loadSummary = useCallback(
    (patientId) => {
      if (!practiceId || !patientId) return;
      const token = ++summaryToken.current;
      setLoadingSummary(true);
      fetchPatientSummary(practiceId, patientId)
        .then((data) => {
          if (token !== summaryToken.current) return;
          setSummary(data);
        })
        .catch((error) => {
          if (token !== summaryToken.current) return;
          setSummary(null);
          setMessage({ type: "error", text: error.message });
        })
        .finally(() => {
          if (token === summaryToken.current) setLoadingSummary(false);
        });
    },
    [practiceId],
  );

  const openPatient = (patientId) => {
    setSelectedId(patientId);
    setStatementOpen(false);
    // Drop the previous patient's record immediately. Leaving it up while the
    // next one loads shows one person's PHI under another person's selection,
    // and a save from that screen targets the newly selected id.
    setSummary(null);
    setMessage(null);
    loadSummary(patientId);
  };

  // Every write refreshes the summary rather than patching state locally: a
  // coverage write also rewrites the patient's cached insurance columns, so the
  // server's answer is the only one that is certainly right.
  const runWrite = (promise, successText, onDone) => {
    setBusy(true);
    setMessage(null);
    promise
      .then(() => {
        setMessage({ type: "success", text: successText });
        if (onDone) onDone();
        loadSummary(selectedId);
      })
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => setBusy(false));
  };

  // Belt and braces with the token above: never render a record whose id does
  // not match the selection, whatever order responses arrive in.
  const shown =
    summary && summary.patient && summary.patient.id === selectedId
      ? summary
      : null;

  if (!selectedPractice) {
    return (
      <div className="patients-page">
        <p className="pt-empty">Select a practice to look up patients.</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-header">
        <div>
          <h1>Patients</h1>
        </div>
        {!readOnly && (
          <button
            type="button"
            className="pt-btn pt-btn-quiet"
            data-testid="duplicates-toggle"
            aria-pressed={showDuplicates}
            onClick={() => setShowDuplicates((on) => !on)}
          >
            {showDuplicates ? "Back to patients" : "Duplicates"}
          </button>
        )}
      </div>

      {message && (
        <div className={`pt-message ${message.type}`}>
          <span>{message.text}</span>
          <button className="pt-message-close" onClick={() => setMessage(null)}>
            &times;
          </button>
        </div>
      )}

      {showDuplicates && !readOnly && (
        <DuplicatesPanel
          practiceId={practiceId}
          isAdmin={!!isAdmin}
          onMerged={() => {
            // The open record may have been folded into another; drop it rather
            // than show a deleted row's PHI under a stale id.
            setSelectedId(null);
            setSummary(null);
            setSearchNonce((n) => n + 1);
          }}
        />
      )}

      {!showDuplicates && (
      <div className="patients-layout">
        <aside className="patients-list">
          <input
            className="patients-search"
            type="search"
            placeholder="Name, date of birth or member ID"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="patients-count">
            <span>
              {searching
                ? "Searching…"
                : `${total.toLocaleString()} patient${total === 1 ? "" : "s"}`}
            </span>
            {total > PAGE_SIZE && (
              <span className="patients-pager">
                <button
                  className="patients-pager-btn"
                  aria-label="Previous patients"
                  disabled={searching || offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  &lsaquo;
                </button>
                <span>
                  {(offset + 1).toLocaleString()}–
                  {Math.min(offset + PAGE_SIZE, total).toLocaleString()}
                </span>
                <button
                  className="patients-pager-btn"
                  aria-label="Next patients"
                  disabled={searching || offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  &rsaquo;
                </button>
              </span>
            )}
          </div>
          <ul>
            {results.map((patient) => (
              <li key={patient.id}>
                <button
                  className={`patients-list-item${patient.id === selectedId ? " is-selected" : ""}`}
                  onClick={() => openPatient(patient.id)}
                >
                  <span className="patients-list-name">
                    {formatPatientName(patient.display_name)}
                  </span>
                  <span className="patients-list-sub">
                    DOB: {patient.patient_dob || patient.subscriber_dob || "—"}
                    {" · "}MRN: {patientMrn(patient) || "—"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {!searching && results.length === 0 && (
            <p className="pt-empty">
              {query
                ? "Nobody matches that."
                : "This practice has no patients yet."}
            </p>
          )}
        </aside>

        <main className="patients-detail">
          {!selectedId && (
            <p className="pt-empty">Pick a patient to see their record.</p>
          )}
          {selectedId && loadingSummary && !shown && (
            <p className="pt-empty">Loading…</p>
          )}
          {shown && (
            <>
              <div className="pt-detail-head">
                <div className="pt-detail-title">
                  <h2>{formatPatientName(shown.patient.display_name)}</h2>
                  {outreachOn && (
                    <PayStatusChip
                      practiceId={practiceId}
                      patientId={shown.patient.id}
                      variant="drawer"
                    />
                  )}
                </div>
                <div className="pt-detail-actions">
                  {readOnly && !statementsOn ? (
                    // The client view says why rather than showing a dead button (Jordan, 2026-10-23).
                    <span className="pt-note">Statements are switched off for this practice — ask Atlas.</span>
                  ) : (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setStatementOpen(true)}
                      disabled={!statementsOn}
                      title={
                        statementsOn
                          ? "Open this patient's statement to print or download"
                          : "Statements are switched off for this practice — ask Atlas"
                      }
                    >
                      Generate statement
                    </button>
                  )}
                </div>
              </div>
              <PatientCard
                key={`pat-${selectedId}`}
                patient={shown.patient}
                canEdit={!readOnly}
                busy={busy}
                onSave={(fields, done) =>
                  runWrite(
                    saveDemographics(practiceId, selectedId, fields),
                    "Patient details saved.",
                    done,
                  )
                }
              />
              <CoverageCard
                // Remount per patient: an open "add coverage" form holding a
                // half-typed member id must not survive a switch and post it
                // against somebody else.
                key={`cov-${selectedId}`}
                coverages={shown.coverages || []}
                pastCoverages={shown.past_coverages || []}
                materialized={shown.coverages_materialized}
                editable={shown.coverages_editable}
                managedBy={shown.coverages_managed_by}
                payers={payers}
                readOnly={readOnly}
                busy={busy}
                onAdd={(payload, done) =>
                  runWrite(
                    addCoverage(practiceId, selectedId, payload),
                    "Coverage added.",
                    done,
                  )
                }
                onCheckEligibility={
                  features.eligibility
                    ? () => {
                        setCheckingEligibility(true);
                        setMessage(null);
                        checkEligibility(practiceId, selectedId)
                          .then((result) =>
                            setEligibility({ patientId: selectedId, ...result }),
                          )
                          .catch((error) =>
                            setMessage({ type: "error", text: error.message }),
                          )
                          .finally(() => setCheckingEligibility(false));
                      }
                    : undefined
                }
                eligibilityResult={
                  eligibility && eligibility.patientId === selectedId
                    ? eligibility
                    : null
                }
                eligibilityBusy={checkingEligibility}
                onReplace={(coverageId, payload, done) =>
                  runWrite(
                    replaceCoverage(practiceId, coverageId, payload),
                    payload.replacement
                      ? "Coverage ended and replaced."
                      : "Coverage ended.",
                    done,
                  )
                }
              />
              {outreachOn && (
                <OutreachTab
                  // Remount per patient: a half-written text must not survive a
                  // switch and be sent to somebody else.
                  key={`outreach-${selectedId}`}
                  practiceId={practiceId}
                  patientId={shown.patient.id}
                />
              )}
              {/* Instance C's panel. Mounted unconditionally per its contract:
                  it renders nothing when the patient has no authorizations and
                  nothing on a 404 or 403, so no gate belongs on this side. Not
                  keyed on selectedId like its neighbours because it holds no
                  form state and guards its own fetch against a patient switch —
                  the neighbours are keyed because they hold half-typed input.

                  The card around it is this page's business, not C's: every
                  other panel in this column is a .pt-card, and without one the
                  authorizations read as having escaped their container. The
                  wrapper collapses when C renders nothing (see the :empty rule),
                  because an empty card is worse than no card. */}
              <div className="pt-card pt-card-optional">
                <PatientAuths practiceId={practiceId} patientId={shown.patient.id} />
              </div>
              <PatientClaims
                key={`claims-${selectedId}`}
                claims={shown.claims || []}
                // Claims, filtered to exactly this patient by id.
                onOpenClaim={() =>
                  openLedgerWith(practiceId, {
                    patient_id: String(shown.patient.id),
                    patient: shown.patient.display_name || "",
                  })
                }
              />
            </>
          )}
        </main>
      </div>
      )}

      {statementOpen && shown && (
        <StatementModal
          practiceId={practiceId}
          patientId={shown.patient.id}
          patientName={shown.patient.display_name}
          onClose={() => setStatementOpen(false)}
        />
      )}

    </div>
  );
}
